import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Loader2 } from 'lucide-react';
import { useOpcoesDaFila } from '@/hooks/usePortabilidade';

const MINIMO = 10;
/** Filas com lista de motivos e ações (as que travam): 15 letras e nada repetido (Douglas 21/10/2026). */
const MINIMO_COM_OPCOES = 15;

/** A justificativa é da FILA em que o item está agora — não é nota solta.
 *  O banco carimba a coluna; a tela só mostra qual é. */
export function JustificativaFilaDialog({ open, onOpenChange, coluna, numeroPedido, razaoSocial,
  justificativaAtual, diasParado, isLoading, onConfirm }: {
  open: boolean; onOpenChange: (open: boolean) => void; coluna: string;
  numeroPedido: string; razaoSocial: string; justificativaAtual?: string | null;
  diasParado?: number | null; isLoading: boolean;
  onConfirm: (justificativa: string, codigos: { motivo: string | null; acao: string | null }) => Promise<unknown>;
}) {
  const [texto, setTexto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [acao, setAcao] = useState('');
  const { data: opcoes = [] } = useOpcoesDaFila(coluna, open);
  const motivos = opcoes.filter(o => o.tipo === 'motivo');
  const acoes = opcoes.filter(o => o.tipo === 'acao');
  const temOpcoes = motivos.length > 0 && acoes.length > 0;
  const minimo = temOpcoes ? MINIMO_COM_OPCOES : MINIMO;
  useEffect(() => { if (open) { setTexto(''); setMotivo(''); setAcao(''); } }, [open]);
  const podeConfirmar = texto.trim().length >= minimo && (!temOpcoes || (!!motivo && !!acao)) && !isLoading;
  const salvar = async () => {
    try {
      await onConfirm(texto.trim(), { motivo: temOpcoes ? motivo : null, acao: temOpcoes ? acao : null });
      onOpenChange(false);
    } catch { /* a mutation mostra o erro; mantém o texto */ }
  };
  return <Dialog open={open} onOpenChange={o => { if (!isLoading) onOpenChange(o); }}>
    <DialogContent className="sm:max-w-[560px]" onClick={e => e.stopPropagation()}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-primary" />Justificar permanência em {coluna}</DialogTitle>
        <DialogDescription>
          {razaoSocial} · pedido {numeroPedido}
          {typeof diasParado === 'number' && diasParado > 0 ? ` · parado há ${diasParado} dia${diasParado > 1 ? 's' : ''}` : ''}
          <br />
          Por que o item continua nesta fila. Fica na timeline como justificativa de {coluna}, não como nota — quem abrir o pedido entende sem perguntar.
        </DialogDescription>
      </DialogHeader>
      {!!justificativaAtual && <div className="rounded-lg border border-border p-3 text-xs">
        <p className="font-semibold">Justificativa atual</p>
        <p className="whitespace-pre-wrap break-words text-muted-foreground">{justificativaAtual}</p>
      </div>}
      {temOpcoes && <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1">
          <Label className="text-xs font-semibold">Por que continua aqui</Label>
          <Select value={motivo} onValueChange={setMotivo} disabled={isLoading}>
            <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Escolha o motivo" /></SelectTrigger>
            <SelectContent>{motivos.map(o => <SelectItem key={o.codigo} value={o.codigo} className="text-xs">{o.rotulo}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid gap-1">
          <Label className="text-xs font-semibold">O que você está fazendo</Label>
          <Select value={acao} onValueChange={setAcao} disabled={isLoading}>
            <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Escolha a ação" /></SelectTrigger>
            <SelectContent>{acoes.map(o => <SelectItem key={o.codigo} value={o.codigo} className="text-xs">{o.rotulo}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>}
      <Label htmlFor="justificativa-fila" className="text-xs font-semibold">Justificativa (obrigatória)</Label>
      <Textarea id="justificativa-fila" value={texto} onChange={e => setTexto(e.target.value)} disabled={isLoading} rows={4}
        placeholder={temOpcoes ? 'O que você viu neste pedido: cotação, o que a Claro mostra, com quem está.' : 'O que falta, com quem está e o que já foi feito.'} />
      <p className="text-xs text-muted-foreground">
        {texto.trim().length < minimo ? `Faltam ${minimo - texto.trim().length} caracteres.` : 'Pronto para salvar.'}
        {temOpcoes && ' Texto igual ao de outro pedido não é aceito.'}
      </p>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>Cancelar</Button>
        <Button onClick={() => void salvar()} disabled={!podeConfirmar}>
          {isLoading ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Salvando…</> : 'Salvar justificativa'}
        </Button>
      </div>
    </DialogContent>
  </Dialog>;
}
